import React from "react";
import PropTypes from "prop-types";
import { httpRequest } from "../../services/http";
import store from "../../state/store";
import isUserLoggedIn from "../../common/helpers/isUserLoggedIn";
import DeclareInterestForm from "./DeclareInterestForm";
import { RenderUrl } from "../atoms/RenderUrl";
import LoginModal from "../LoginModal";
import Loader from "../Loader";
import Events from "../../common/helpers/event-helpers/events";
import helpers from "../../common/utils/utils";
import AddToast from "../../state/actions/AddToast";
import Modal from "../atoms/Modal";
import { CONNECTION_REQUEST_ERROR } from "./constants";
import GeneWithRefGenomeLink from "../../common/helpers/GeneWithRefGenomeLink";
import { approxWhisper, gray888 } from "../../common/helpers/colors";
import HelperText from "../atoms/HelperText";

class DeclarationList extends React.Component {
  constructor() {
    super();
    this.componentName = "DeclareInterestForm";
    // eslint-disable-next-line react/no-unused-state
    this.state = { data: { notes: "" } };
  }

  componentDidMount() {
    this.getDeclarations();
    Events.subscribe("DECLARATION_MODIFIED", this, this.getDeclarations, []);
  }

  getDeclarations() {
    const state = store.getState();
    const { variantId, genes, userId } = this.props;

    this.setState({ loadingDeclarations: true });
    let url = `${state.apiUrl}/declaration_of_interest/`;
    const queryOptions = {};
    if (variantId) {
      queryOptions.variant_id = variantId;
    }
    if (genes) {
      queryOptions.genes = genes.map((g) => g.symbol).join(",");
    }
    if (userId) {
      queryOptions.user_id = userId;
    }
    url += `?${helpers.objectToQueryString(queryOptions)}`;
    httpRequest
      .get(url)
      .then((response) => {
        const newState = {};
        const { data } = response;
        newState.declarations = data.declarations;
        if (data.variant_id) {
          newState.variant_id = data.variant_id;
        }
        if (data.genes) {
          newState.genes = data.genes;
        }

        this.setState(newState);
      })
      .catch((error) => {
        console.error("While getting declarations:", error);
      })
      .finally(() => {
        this.setState({ loadingDeclarations: false });
      });
  }

  saveClick = (declaration) => {
    // just log that form opened, no json_data
    const state = store.getState();
    const url = `${state.apiUrl}/interest_connect/`;

    const data = {};
    data.event_type = "click";
    data.declaration_id = declaration.id;
    if (state.variantResult && state.variantResult.query_id) {
      data.query_id = state.variantResult.query_id;
    }
    if (state.variantResult && state.variantResult.variant_id) {
      data.variant_id = state.variantResult.variant_id;
    }
    httpRequest
      .post(url, data)
      .then(() => {})
      .catch((error) => {
        console.error("Error saving click:", error);
      });
  };

  toggleLoginModal() {
    const { showLoginModal } = this.state;
    this.setState({ showLoginModal: !showLoginModal });
  }

  toggleModal(declaration) {
    const { modalOpen } = this.state;
    const isModalOpen = !modalOpen;
    const state = {
      loading: false,
      error: null,
      accept: false,
      declaration: null,
      modalOpen: isModalOpen,
    };
    if (declaration) {
      state.declaration = declaration;
    }
    this.setState(state);
    // If it's an open, save an entry with no json_data
    if (isModalOpen) this.saveClick(declaration);
  }

  saveEntry() {
    const state = store.getState();
    const { declaration } = this.state;
    const url = `${state.apiUrl}/interest_connect/`;

    const newState = { error: null, loading: true };
    this.setState(newState);

    const data = {};
    if (state.variantResult && state.variantResult.query_id) {
      data.query_id = state.variantResult.query_id;
    }
    data.event_type = "submit";
    data.declaration_id = declaration.id;
    httpRequest
      .post(url, data)
      .then(() => {
        AddToast("Connection request submitted!", "success");
        this.toggleModal();
      })
      .catch((error) => {
        console.error("Error submitting connection request", error);
        this.setState({
          error: CONNECTION_REQUEST_ERROR,
        });
      });
  }

  deleteEntry(declaration) {
    const state = store.getState();
    const url = `${state.apiUrl}/declaration_of_interest/`;

    declaration.deleteLoading = true;
    this.forceUpdate();

    const data = {};
    if (state.variantResult && state.variantResult.query_id) {
      data.query_id = state.variantResult.query_id;
    }
    data.declaration_id = declaration.id;
    httpRequest
      .delete(url, data)
      .then(() => {
        AddToast("Deleted!", "success");
        Events.dispatch("DECLARATION_MODIFIED");
      })
      .catch(() => {
        console.error("Error submitting deletion request");
        this.setState({
          error: "Error submitting deletion request. Please contact support.",
        });
      });
  }

  render() {
    const {
      accept,
      declaration,
      modalOpen,
      loading,
      emailText,
      error,
      declarations,
      loadingDeclarations,
      genes,
      showLoginModal,
    } = this.state;
    let tmpl = null;
    const { emptyDeclarationMessage, userId, variantId } = this.props;

    const controls = (
      <div
        className="varsome-modal-controls varsome-clearfix tw-pt-4"
        style={{ borderTop: "1px solid #ddd" }}
      >
        <button
          type="button"
          className="btn btn-danger varsome-left"
          onClick={() => this.toggleModal()}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-success varsome-right"
          onClick={() => this.saveEntry()}
          disabled={!accept}
        >
          Submit{" "}
          <i className="varsome-icons icon-chevron-right tw-ml-2 tw-text-xs" />
        </button>
      </div>
    );

    // Get pretty_spec (hgvs or gene name)
    const prettySpec =
      declaration &&
      (declaration.pretty_spec
        ? declaration.pretty_spec
        : `all variants on gene ${declaration.object_value}`);
    const modal = (
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => this.toggleModal()}
        ContentRender={() => {
          return (
            <div className="tw-p-6" style={{ maxWidth: "1100px" }}>
              <h4>Connect with user</h4>
              {!loading && (
                <div className="varsome-modal-content">
                  <div className="row">
                    <div className="col-12 col-xs-12">
                      <p style={{ fontSize: "1.1rem" }}>
                        If you submit this form, both you and the user who
                        initiated the connection request will receive the
                        following email:
                      </p>
                    </div>
                    <div className="col-12 col-xs-12">
                      <form>
                        <div className="row">
                          <div className="col-12 col-xs-12">
                            <div>
                              <label
                                className="tw-p-4 tw-whitespace-pre"
                                style={{
                                  backgroundColor: approxWhisper,
                                  border: "1px solid #ddd",
                                }}
                              >
                                {`Dear <name>,
                                  You have previously declared your interest to connect with other users on ${prettySpec}.

                                  User ${
                                    store.getState().userName
                                  } (in CC) has requested to connect with you.

                                  We will let you introduce yourselves, your goals and your interests directly to each other, and explore a collaboration.
                                  You do not have to keep us informed; however, if you do pursue a collaboration, we would love to know about it. Please also keep us in mind for a public acknowledgement.

                                  We sincerely wish you the best possible outcome.

                                  Best regards
                                  the VarSome team`}
                              </label>
                              <div>{emailText}</div>
                            </div>
                          </div>
                        </div>
                        <div className="row tw-mt-4">
                          <div className="col-12 col-xs-12">
                            <label
                              className="tw-cursor-pointer"
                              htmlFor="accept"
                              onClick={() => {
                                this.setState({ accept: !accept });
                              }}
                            >
                              <input
                                type="checkbox"
                                name="accept"
                                checked={accept}
                                onChange={(e) => {
                                  this.setState({ accept: e.target.checked });
                                }}
                              />
                              &nbsp;&nbsp;I consent to the message above being
                              e-mailed to the other user who wishes to explore a
                              collaboration (and to send it to me too).
                              <span className="tw-text-xs tw-text-gray-400">
                                E-mail addresses between collaborating parties
                                will not be shared by Saphetor.
                              </span>
                            </label>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
              {!loading && controls}
              {loading && (
                <div>
                  {!error && (
                    <div
                      className="varsome-text-center tw-text-2xl tw-pt-20 tw-px-0 tw-pb-0"
                      style={{
                        color: gray888,
                      }}
                    >
                      Please wait...
                    </div>
                  )}
                  {error && (
                    <div className="varsome-request-error pubmed-form-error tw-text-center">
                      {error}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }}
      />
    );

    if (Array.isArray(declarations)) {
      if (declarations.length > 0) {
        tmpl = (
          <div className="tw-mb-4">
            <h5>
              Connection requests{" "}
              <HelperText fieldDescriptionKey="Connection requests" />
            </h5>
            <table className="varsome-table">
              <thead>
                <tr>
                  <th width="20%">About variant</th>
                  <th width="70%">Message</th>
                  <th width="10%" />
                </tr>
              </thead>
              <tbody>
                {declarations.map((dec, i) => {
                  const url = dec.pretty_spec
                    ? `${store.getState().varsomeHost}/variant/${
                        dec.object_value
                      }`
                    : `${store.getState().varsomeHost}/gene/${
                        dec.object_value
                      }`;
                  return (
                    <tr key={i}>
                      <td>
                        {dec.pretty_spec ? (
                          <RenderUrl url={url} label={dec.pretty_spec} />
                        ) : (
                          <div>
                            all on gene{" "}
                            <GeneWithRefGenomeLink
                              style={{ wordWrap: "break-word" }}
                              href={url}
                              goInternally={true}
                            >
                              {dec.object_value}
                            </GeneWithRefGenomeLink>
                          </div>
                        )}
                      </td>
                      <td>{dec.notes}</td>
                      <td className="tw-text-center">
                        {isUserLoggedIn() && dec.can_edit ? (
                          <button
                            type="button"
                            style={{ fontSize: "0.8rem" }}
                            onClick={() => this.deleteEntry(dec)}
                            className="btn btn-danger"
                          >
                            {dec.deleteLoading ? (
                              <i
                                className="fa fa-circle-o-notch fa-spin"
                                style={{ color: "white" }}
                              />
                            ) : (
                              "Delete"
                            )}
                          </button>
                        ) : (
                          <a
                            onClick={() =>
                              isUserLoggedIn()
                                ? this.toggleModal(dec)
                                : this.toggleLoginModal()
                            }
                          >
                            Connect with this user
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      } else if (declarations.length === 0 && emptyDeclarationMessage) {
        tmpl = <div>{emptyDeclarationMessage}</div>;
      }
    } else if (loadingDeclarations) {
      tmpl = (
        <div className="varsome-text-center">
          <Loader size="small" />
        </div>
      );
    }

    let connectionBtn;

    if (
      !userId &&
      !loadingDeclarations &&
      store.getState().queryType !== "gene"
    ) {
      connectionBtn = (
        <div className="varsome-connect-button">
          <DeclareInterestForm
            variantId={variantId}
            genes={genes}
            text="Connect with past and future viewers of this variant..."
          />
        </div>
      );
    }

    if (
      !userId &&
      !loadingDeclarations &&
      store.getState().queryType === "gene" &&
      genes &&
      genes.length > 0
    ) {
      connectionBtn = (
        <div className="varsome-connect-button">
          <DeclareInterestForm
            variantId={variantId}
            genes={genes}
            text="Connect with past and future viewers of all variants on this gene..."
          />
        </div>
      );
    }

    return (
      <div>
        {modal}
        <LoginModal
          showModal={showLoginModal}
          toggleModal={() => this.toggleLoginModal()}
        />
        {connectionBtn}
        {tmpl}
      </div>
    );
  }
}

DeclarationList.propTypes = {
  variantId: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  genes: PropTypes.arrayOf(PropTypes.any),
  userId: PropTypes.string,
  emptyDeclarationMessage: PropTypes.string,
};

DeclarationList.defaultProps = {
  variantId: null,
  genes: null,
  userId: null,
  emptyDeclarationMessage: null,
};

export default DeclarationList;
